import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { formatCameraLabel, getCameraKeyString, getSiteName, getSiteString } from "@src/utils/key-parser";

export type CameraSortOption = "id" | "name" | "site";
export type CameraSortDirection = "asc" | "desc";

export function sortCameras(
  cameras: CameraApiResponse[],
  sortBy: CameraSortOption,
  siteNames?: ReadonlyMap<string, string>,
  direction: CameraSortDirection = "asc",
): CameraApiResponse[] {
  const sign = direction === "desc" ? -1 : 1;
  const sorted = [...cameras];
  sorted.sort((a, b) => sign * compareByOption(a, b, sortBy, siteNames));
  return sorted;
}

export function compareByOption(
  a: CameraApiResponse,
  b: CameraApiResponse,
  option: CameraSortOption,
  siteNames?: ReadonlyMap<string, string>,
): number {
  const aKey = getCameraKeyString(a);
  const bKey = getCameraKeyString(b);

  switch (option) {
    case "id":
      return aKey.localeCompare(bKey);
    case "name": {
      // Matches the fallback used to display the camera's name (CameraListItem), so
      // sort order lines up with what's actually shown for cameras without a name.
      const aName = a.name || formatCameraLabel(aKey) || aKey;
      const bName = b.name || formatCameraLabel(bKey) || bKey;
      return aName.localeCompare(bName);
    }
    case "site": {
      // Sort by the site's nickname (display name); fall back to the raw site
      // id for sites that don't have one, matching what's shown elsewhere.
      const aSiteId = getSiteString(a);
      const bSiteId = getSiteString(b);
      const aSiteName = getSiteName(a, aSiteId, siteNames).name;
      const bSiteName = getSiteName(b, bSiteId, siteNames).name;
      if (aSiteName !== bSiteName) {
        return aSiteName.localeCompare(bSiteName);
      }
      // Two different sites can share a display name; fall back to the site
      // id first so cameras from the same site stay contiguous, and only
      // use the camera key to order cameras within the same site.
      if (aSiteId !== bSiteId) {
        return aSiteId.localeCompare(bSiteId);
      }
      return aKey.localeCompare(bKey);
    }
  }
}
